/* ============================================================
   ProviderProductEditPage — 0e895f9a (a1a3b8f4 part 2): seller
   listing create/edit on a separate page (was the inline
   ProviderProductsPanel form). Two columns (≥1024px, stacks on
   narrow screens): left = media stage + uploads w/ reorder, right =
   settings, parameters and the priced options editor. Pure form
   model lives in providerProductEditView.ts.
   ============================================================ */

import { useEffect, useState } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import type { ProductCategoryNode } from '../types/category';
import type { Material } from '../types';
import type { ProductDraft, ProductMediaItem } from '../types/product';
import { MediaGallery } from './MediaGallery';
import { ProductOptionsEditor } from './ProductOptionsEditor';
import {
  EMPTY_FORM,
  MAX_MEDIA,
  buildEditForm,
  canSubmitProduct,
  mediaUploadError,
  moveMediaItem,
  normalizeProductDraft,
  removeMediaAt,
} from './providerProductEditView';

import { isViewableModelUrl } from './shop3dView';

const MEDIA_ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,.stl,.3mf,.obj';

interface Props {
  /** null = create mode; guid = edit an existing own listing. */
  productId: string | null;
  onClose: () => void;
}

export function ProviderProductEditPage({ productId, onClose }: Props) {
  const [form, setForm] = useState<ProductDraft>(EMPTY_FORM);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [categories, setCategories] = useState<ProductCategoryNode[]>([]);
  const [loading, setLoading] = useState(productId !== null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.getMaterials().then(setMaterials).catch(() => {});
    api.getCategories().then(setCategories).catch(() => {});
    if (productId === null) return;
    // Deep-link refresh: resolve the own listing from /products/mine
    // (full option rows — the public view hides disabled options).
    setLoading(true);
    setLoadError(null);
    api.getMyProducts()
      .then((items) => {
        const found = items.find((p) => p.productId === productId);
        if (!found) throw new Error('Товар не найден среди ваших товаров.');
        setForm(buildEditForm(found));
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить товар'))
      .finally(() => setLoading(false));
  }, [productId]);

  const patch = (p: Partial<ProductDraft>) => {
    setDirty(true);
    setForm((f) => ({ ...f, ...p }));
  };

  const requestClose = () => {
    if (dirty && !busy && !window.confirm('Закрыть без сохранения? Несохранённые изменения будут потеряны.')) {
      return;
    }
    onClose();
  };

  // ME-3: upload gallery files through the engine, append as ordered items.
  const addMediaFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const roomError = mediaUploadError(form.media?.length ?? 0, files.length, MAX_MEDIA);
    if (roomError) {
      setFormError(roomError);
      return;
    }
    setUploading(true);
    setFormError(null);
    try {
      const added: ProductMediaItem[] = [];
      for (const file of Array.from(files)) {
        const uploaded = await api.uploadMedia(file);
        if (uploaded.kind !== 'image' && uploaded.kind !== 'video' && uploaded.kind !== 'model') {
          throw new Error('Для галереи подходят только фото и видео.');
        }
        // cca635b3: model rows ride the gallery as URL items — the stored kind
        // label stays 'image' (the gallery API contract has image/video only);
        // the shop viewer derives the 3D stage from the URL extension.
        added.push({
          url: uploaded.url,
          thumbnailUrl: uploaded.kind === 'model' ? null : uploaded.thumbnailUrl,
          kind: uploaded.kind === 'model' ? 'image' : uploaded.kind,
        });
      }
      patch({ media: [...(form.media ?? []), ...added] });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось загрузить файлы');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setFormError(null);
    const payload = normalizeProductDraft(form);
    try {
      if (productId !== null) {
        await api.updateProduct(productId, payload);
      } else {
        await api.createProduct(payload);
      }
      onClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    height: 44,
    padding: '0 var(--space-md)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    background: 'var(--color-surface)',
    fontSize: 'var(--fs-body)',
    width: '100%',
  };

  const thumbButton = (label: string, ariaLabel: string, onClick: () => void, disabled = false): React.ReactNode => (
    <button
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '1px solid var(--color-separator)',
        background: 'var(--color-surface)',
        fontSize: 10,
        lineHeight: 1,
        cursor: disabled ? 'default' : 'pointer',
        color: 'var(--color-text-primary)',
        opacity: disabled ? 0.35 : 1,
      }}
    >
      {label}
    </button>
  );

  if (loadError) {
    return (
      <div>
        <button onClick={onClose} style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-md)', fontSize: 'var(--fs-body)' }}>
          ← Мои товары
        </button>
        <div className="card" style={{ padding: 'var(--space-xl)', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger)' }}>{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)', flexWrap: 'wrap' }}>
        <button onClick={requestClose} style={{ color: 'var(--color-primary)', fontSize: 'var(--fs-body)' }}>
          ← Мои товары
        </button>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>
          {productId === null ? 'Новый товар' : `Редактирование: ${form.title || '…'}`}
        </h2>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 'var(--space-2xl)', textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : (
        <div className="product-edit-layout">
          {/* ── Left: media stage + uploads (0e895f9a) ── */}
          <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)', alignContent: 'start' }}>
            <MediaGallery
              product={{
                media: form.media,
                imageUrl: form.imageUrl,
                title: form.title.trim() || 'Т',
              }}
            />

            <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  Фото, видео и 3D-модель (STL/OBJ/3MF) · до {MAX_MEDIA} файлов
                  {(form.media?.length ?? 0) > 0 ? ` · добавлено ${form.media?.length ?? 0}` : ''}
                </span>
                <label
                  className="btn btn-secondary"
                  style={{ height: 36, padding: '0 var(--space-md)', cursor: uploading ? 'wait' : 'pointer' }}
                >
                  {uploading ? 'Загрузка…' : '+ Загрузить'}
                  <input
                    type="file"
                    accept={MEDIA_ACCEPT}
                    multiple
                    disabled={uploading}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      void addMediaFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {(form.media?.length ?? 0) > 0 && (
                <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', marginBottom: 12 }}>
                  {(form.media ?? []).map((m: ProductMediaItem, i: number) => (
                    <div key={`${m.url}-${i}`} style={{ position: 'relative', width: 64, height: 64 }}>
                      {isViewableModelUrl(m.url) ? (
                        // cca635b3: model rows have no raster thumb — a 3D tile.
                        <span
                          aria-hidden
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            background: '#F2F2F7',
                            color: 'var(--color-primary)',
                            fontSize: 'var(--fs-caption)',
                            fontWeight: 'var(--fw-semibold)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--color-separator)',
                          }}
                        >
                          3D
                        </span>
                      ) : (
                      <img
                        src={resolveMediaUrl(m.thumbnailUrl ?? m.url)}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--color-separator)',
                        }}
                      />
                      )}
                      {m.kind === 'video' && (
                        <span
                          style={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            fontSize: 'var(--fs-caption)',
                            background: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            borderRadius: 'var(--radius-sm)',
                            padding: '1px 5px',
                          }}
                        >
                          ▶
                        </span>
                      )}
                      {/* 0e895f9a: per-thumb reorder + remove (ordered gallery) */}
                      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: -10, display: 'flex', gap: 4 }}>
                        {thumbButton('◀', 'Сдвинуть влево', () => patch({ media: moveMediaItem(form.media ?? [], i, -1) }), i === 0)}
                        {thumbButton('▶', 'Сдвинуть вправо', () => patch({ media: moveMediaItem(form.media ?? [], i, 1) }), i === (form.media?.length ?? 0) - 1)}
                      </div>
                      <button
                        onClick={() => patch({ media: removeMediaAt(form.media ?? [], i) })}
                        aria-label="Удалить"
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: '1px solid var(--color-separator)',
                          background: 'var(--color-surface)',
                          fontSize: 12,
                          lineHeight: 1,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)' }}>
                Первое фото — обложка в магазине. Порядок можно менять стрелками.
              </p>
            </div>
          </div>

          {/* ── Right: settings + parameters + priced options ── */}
          <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-sm)', alignContent: 'start' }}>
            <input style={fieldStyle} placeholder="Название *" value={form.title}
                   onChange={(e) => patch({ title: e.target.value })} />
            <textarea
              style={{ ...fieldStyle, height: 88, padding: 'var(--space-sm) var(--space-md)', resize: 'vertical' }}
              placeholder="Описание"
              value={form.description ?? ''}
              onChange={(e) => patch({ description: e.target.value })}
            />

            {/* 3d4c488f: shop taxonomy assignment */}
            <select style={fieldStyle} value={form.productCategoryId ?? ''}
                    onChange={(e) => patch({ productCategoryId: e.target.value || null })}>
              <option value="">Без категории</option>
              {categories.map((root) => (
                <optgroup key={root.id} label={root.name}>
                  <option value={root.id}>{root.name}</option>
                  {root.children.map((sub) => (
                    <option key={sub.id} value={sub.id}>{root.name} → {sub.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-sm)' }}>
              <input style={fieldStyle} type="number" min={1} placeholder="Цена, ₽ *" value={form.priceRub || ''}
                     onChange={(e) => patch({ priceRub: Number(e.target.value) })} />
              <input style={fieldStyle} type="number" min={0} placeholder="Цена до скидки"
                     value={form.compareAtPriceRub ?? ''}
                     onChange={(e) => patch({ compareAtPriceRub: e.target.value ? Number(e.target.value) : null })} />
              <select style={fieldStyle} value={form.materialId ?? ''}
                      onChange={(e) => patch({ materialId: e.target.value })}>
                <option value="">Материал не указан</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <input style={fieldStyle} placeholder="Цвет" value={form.color ?? ''}
                     onChange={(e) => patch({ color: e.target.value })} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 'var(--space-sm)' }}>
              <label style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', display: 'grid', gap: 4 }}>
                Остаток (пусто = ∞)
                <input style={fieldStyle} type="number" min={0} value={form.stockCount ?? ''}
                       onChange={(e) => patch({ stockCount: e.target.value ? Number(e.target.value) : null })} />
              </label>
              <label style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', display: 'grid', gap: 4 }}>
                Мин. кол-во
                <input style={fieldStyle} type="number" min={1} value={form.minOrderQuantity}
                       onChange={(e) => patch({ minOrderQuantity: Number(e.target.value) })} />
              </label>
              <label style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', display: 'grid', gap: 4 }}>
                Макс. кол-во
                <input style={fieldStyle} type="number" min={1} value={form.maxOrderQuantity ?? ''}
                       onChange={(e) => patch({ maxOrderQuantity: e.target.value ? Number(e.target.value) : null })} />
              </label>
              <label style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', display: 'grid', gap: 4 }}>
                Срок, дней
                <input style={fieldStyle} type="number" min={1} max={60} value={form.productionDays}
                       onChange={(e) => patch({ productionDays: Number(e.target.value) })} />
              </label>
            </div>

            {/* a1a3b8f4: priced options editor (покраска, сборка, цвет…) */}
            <ProductOptionsEditor
              options={form.options ?? []}
              onChange={(next) => patch({ options: next })}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', fontSize: 'var(--fs-body)' }}>
              <input type="checkbox" checked={form.publish} onChange={(e) => patch({ publish: e.target.checked })} />
              Опубликовать в магазине
            </label>

            {formError && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--fs-caption)' }}>{formError}</p>}

            <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={requestClose} disabled={busy}>
                Отмена
              </button>
              <button
                className="btn btn-primary"
                onClick={() => void submit()}
                disabled={busy || !canSubmitProduct(form)}
              >
                {busy ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
